import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Rocket, Scale, Files, Users, PlayCircle, Lightbulb, ChevronRight, Clock, LucideIcon, Loader2 } from 'lucide-react';
import { getLanguageDirection } from '../../i18n/config';
import { useOrgSlug, buildOrgHomeUrl } from '../../utils/urlHelpers';
import type { DocCategory, DocPage } from './docsContent';
import { cn } from '../../lib/utils';

interface DocsUI { back: string; docs: string; watch: string; soonTitle: string; soonBody: string; }
interface DocsContent { ui: DocsUI; categories: DocCategory[]; }

const CATEGORY_ICON: Record<DocCategory['icon'], LucideIcon> = {
  rocket: Rocket, scale: Scale, files: Files, users: Users,
};

// Videos/posters can be served from the app origin (default) or an external
// bucket/CDN. Set VITE_DOCS_ASSET_BASE (e.g. https://storage.googleapis.com/omnilaw-docs-assets)
// to serve them from there without changing content. Relative paths in the
// content JSON (e.g. /docs/videos/clip1.mp4) are prefixed with this base.
const ASSET_BASE = (import.meta.env.VITE_DOCS_ASSET_BASE || '').replace(/\/$/, '');
// Cache-buster: videos are served with long-lived `immutable` caching, so when a
// clip is re-rendered under the same filename, bump this version (or VITE_DOCS_ASSET_VERSION)
// to force browsers/CDN to fetch the new file.
const ASSET_VER = import.meta.env.VITE_DOCS_ASSET_VERSION || '2';
const assetUrl = (path: string) => {
  const url = ASSET_BASE && path.startsWith('/') ? `${ASSET_BASE}${path}` : path;
  return `${url}${url.includes('?') ? '&' : '?'}v=${ASSET_VER}`;
};

// Load the localized docs content for the active language (falls back to English).
// Returns null if everything fails so the caller can show an error state instead of hanging.
async function loadContent(lang: string): Promise<DocsContent | null> {
  const base = (lang || 'en').split('-')[0];
  for (const file of [base, 'en']) {
    try {
      const res = await fetch(`/docs/i18n/${file}.json`);
      if (res.ok) return await res.json();
    } catch { /* try next */ }
  }
  return null;
}

const VideoPlayer: React.FC<{ page: DocPage }> = ({ page }) => {
  if (!page.video) return null;
  return (
    <div className="rounded-xl overflow-hidden border border-border shadow-lg bg-navy-dark">
      <video
        className="w-full aspect-video bg-navy-dark"
        src={assetUrl(page.video.src)}
        poster={assetUrl(page.video.poster)}
        controls
        preload="none"
        controlsList="nodownload"
      />
    </div>
  );
};

const Documentation: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dir = getLanguageDirection(i18n.language);
  const isRTL = dir === 'rtl';
  const flip = isRTL ? { transform: 'rotate(180deg)' } : undefined;
  const navigate = useNavigate();
  const orgSlug = useOrgSlug();

  const [content, setContent] = useState<DocsContent | null>(null);
  const [activeId, setActiveId] = useState<string>('');
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    loadContent(i18n.language).then(c => {
      if (cancelled) return;
      if (c && c.categories?.[0]?.pages?.[0]) {
        setContent(c);
        setActiveId(prev => prev || c.categories[0].pages[0].id);
      } else {
        setFailed(true);
      }
    });
    return () => { cancelled = true; };
  }, [i18n.language, reloadKey]);

  const allPages = useMemo(
    () => (content ? content.categories.flatMap(c => c.pages.map(p => ({ page: p, category: c }))) : []),
    [content]
  );

  if (!content || !activeId) {
    if (failed) {
      return (
        <div dir={dir} className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-5 px-6 text-center">
          <ShieldMark />
          <p className="max-w-sm text-muted-foreground">
            {t('errors.docs.loadFailed', "We couldn't load the documentation. Please check your connection and try again.")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => setReloadKey(k => k + 1)}
              className="rounded-lg bg-gold px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gold-bright"
            >
              {t('common.actions.tryAgain', 'Try again')}
            </button>
            <button
              onClick={() => navigate(buildOrgHomeUrl(orgSlug))}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              {t('common.actions.backToWorkspace', 'Back to workspace')}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  const active = allPages.find(p => p.page.id === activeId) || allPages[0];
  const { page, category } = active;
  const ui = content.ui;

  return (
    <div dir={dir} className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-20 flex items-center gap-3 px-6 h-16 border-b border-border bg-background/95 backdrop-blur">
        <button
          onClick={() => navigate(buildOrgHomeUrl(orgSlug))}
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={18} style={flip} />
          <span>{ui.back}</span>
        </button>
        <div className="mx-2 h-5 w-px bg-border" />
        <div className="flex items-center gap-2">
          <ShieldMark />
          <h1 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>{ui.docs}</h1>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl">
        {/* Sidebar nav */}
        <nav className="hidden md:block w-72 shrink-0 border-e border-border py-6 pe-4 sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto">
          {content.categories.map(cat => {
            const Icon = CATEGORY_ICON[cat.icon];
            return (
              <div key={cat.id} className="mb-6">
                <div className="flex items-center gap-2 px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Icon size={14} className="text-gold" />
                  <span>{cat.title}</span>
                </div>
                <ul className="space-y-0.5">
                  {cat.pages.map(p => {
                    const isActive = p.id === activeId;
                    return (
                      <li key={p.id}>
                        <button
                          onClick={() => setActiveId(p.id)}
                          className={cn(
                            'group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-start transition-colors',
                            isActive ? 'bg-gold/10 text-gold font-medium' : 'text-foreground/80 hover:bg-accent hover:text-foreground'
                          )}
                        >
                          {p.video
                            ? <PlayCircle size={15} className={isActive ? 'text-gold' : 'text-muted-foreground group-hover:text-foreground'} />
                            : <ChevronRight size={15} style={flip} className={isActive ? 'text-gold' : 'text-muted-foreground group-hover:text-foreground'} />}
                          <span className="flex-1">{p.title}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* Content */}
        <main className="flex-1 min-w-0 px-6 md:px-10 py-10">
          <div className="mx-auto max-w-3xl">
            {/* Mobile page picker (the sidebar nav is hidden below md) */}
            <div className="md:hidden mb-6">
              <select
                value={activeId}
                onChange={(e) => setActiveId(e.target.value)}
                aria-label={ui.docs}
                className="w-full rounded-lg border border-border bg-background text-foreground px-3 py-2 text-sm"
              >
                {content.categories.map(cat => (
                  <optgroup key={cat.id} label={cat.title}>
                    {cat.pages.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
              <span>{category.title}</span>
            </div>
            <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: 'var(--font-display)' }}>{page.title}</h2>
            <p className="text-lg text-muted-foreground mb-6">{page.summary}</p>

            {page.video ? (
              <div className="mb-8">
                <VideoPlayer key={page.id} page={page} />
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock size={13} />
                  <span>{page.video.duration}</span>
                  <span className="h-1 w-1 rounded-full bg-current opacity-40" />
                  <span>{ui.watch}</span>
                </div>
              </div>
            ) : page.comingSoon ? (
              <div className="mb-8 flex items-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gold/10">
                  <PlayCircle size={20} className="text-gold" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{ui.soonTitle}</p>
                  <p className="text-xs text-muted-foreground">{ui.soonBody}</p>
                </div>
              </div>
            ) : null}

            <article className="space-y-7">
              {page.sections.map((s, i) => (
                <section key={i}>
                  {s.heading && <h3 className="text-lg font-semibold mb-2 text-foreground">{s.heading}</h3>}
                  {s.body && <p className="text-[15px] leading-relaxed text-foreground/85">{s.body}</p>}
                  {s.bullets && (
                    <ul className="mt-2 space-y-2">
                      {s.bullets.map((b, j) => (
                        <li key={j} className="flex gap-2.5 text-[15px] leading-relaxed text-foreground/85">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.tip && (
                    <div className="mt-3 flex gap-3 rounded-lg border border-gold/20 bg-gold/5 p-3">
                      <Lightbulb size={18} className="mt-0.5 shrink-0 text-gold" />
                      <p className="text-sm text-foreground/85">{s.tip}</p>
                    </div>
                  )}
                </section>
              ))}
            </article>

            <PagerNav allPages={allPages} activeId={activeId} onSelect={setActiveId} flip={flip} />
          </div>
        </main>
      </div>
    </div>
  );
};

type PageEntry = { page: DocPage; category: DocCategory };
const PagerNav: React.FC<{ allPages: PageEntry[]; activeId: string; onSelect: (id: string) => void; flip?: React.CSSProperties }> = ({ allPages, activeId, onSelect, flip }) => {
  const idx = allPages.findIndex(p => p.page.id === activeId);
  const prev = idx > 0 ? allPages[idx - 1] : null;
  const next = idx < allPages.length - 1 ? allPages[idx + 1] : null;
  return (
    <div className="mt-12 flex items-center justify-between border-t border-border pt-6">
      {prev ? (
        <button onClick={() => onSelect(prev.page.id)} className="group flex items-center gap-2 text-start text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={16} style={flip} />
          <span>{prev.page.title}</span>
        </button>
      ) : <span />}
      {next ? (
        <button onClick={() => onSelect(next.page.id)} className="group flex items-center gap-2 text-end text-sm text-gold hover:text-gold-bright">
          <span>{next.page.title}</span>
          <ChevronRight size={16} style={flip} />
        </button>
      ) : <span />}
    </div>
  );
};

const ShieldMark: React.FC = () => (
  <svg width={22} height={22} viewBox="0 0 100 100" fill="none" aria-hidden>
    <path d="M50 6 L86 20 V50 C86 74 68 88 50 94 C32 88 14 74 14 50 V20 Z" fill="var(--navy)" stroke="var(--gold)" strokeWidth="4" />
    <g stroke="var(--gold)" strokeWidth="4" strokeLinecap="round">
      <line x1="50" y1="28" x2="50" y2="64" />
      <line x1="34" y1="42" x2="66" y2="42" />
      <line x1="40" y1="66" x2="60" y2="66" />
    </g>
  </svg>
);

export default Documentation;
